import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { ATTACHMENT_POLICY_STANDARD } from '#/api/platform/attachments';
import { purchaseOrderAttachmentsApi } from '#/api/srm/purchaseOrder/attachments';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import { cn } from '#/lib/utils';
import PageShell from '#/modules/infra/layout/PageShell';
import PurchaseOrderBlock from '#/modules/srmMgmt/purchaseOrder/components/PurchaseOrderBlock';
import { getPurchaseOrderApiErrorMessage } from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import { purchaseOrderDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const PurchaseOrderDetailPage = () => {
  const { id } = useParams();

  const detailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(id),
    queryFn: () => fetchPurchaseOrderDetail(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getPurchaseOrderApiErrorMessage(detailQuery.error, '載入物料採購單失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">物料採購單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrder}>返回列表</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getPurchaseOrderApiErrorMessage(detailQuery.error, '載入物料採購單失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <PurchaseOrderBlock
                detail={detail}
                showActions
                onRefetch={() => detailQuery.refetch()}
              />
              <DocumentAttachmentsCard
                businessId={String(detail.id)}
                ownerKey="purchaseOrder"
                api={purchaseOrderAttachmentsApi}
                policy={ATTACHMENT_POLICY_STANDARD}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default PurchaseOrderDetailPage;
